import { useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

interface UseFileUploadProps {
  componentName?: string;
  projectSlug?: string;
  s3Bucket?: string;
  s3Prefix?: string;
  tags: string;
  uploadId: string;
  formData?: string[];
  onChange: (files: string[]) => void;
  onError: (message: string) => void;
}

interface UploadFileResult {
  success: boolean;
  uploadedFiles?: string[];
  error?: string;
}

interface RemoveFileResult {
  success: boolean;
  error?: string;
}

export const useFileUpload = ({
  componentName,
  projectSlug,
  s3Bucket,
  s3Prefix,
  tags,
  uploadId,
  formData,
  onChange,
  onError,
}: UseFileUploadProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);

  const uploadFiles = async (files: FileList): Promise<UploadFileResult> => {
    if (!componentName && !projectSlug) {
      const error = 'Component name or project slug is required';
      onError(error);
      return { success: false, error };
    }

    if (!files || files.length === 0) {
      return { success: false, error: 'No files selected' };
    }

    setIsLoadingFiles(true);

    try {
      const uploadedFiles: string[] = [];
      const uploadFormData = new FormData();

      uploadFormData.append('s3Bucket', s3Bucket || '');
      uploadFormData.append('s3Prefix', s3Prefix || '');
      uploadFormData.append('tagString', tags);
      uploadFormData.append('componentName', componentName || projectSlug || '');
      uploadFormData.append('uploadId', uploadId);

      // Validate file sizes and add to FormData
      for (const file of Array.from(files)) {
        if (file.size > 60 * 1024 * 1024) {
          const error = `File ${file.name} exceeds the maximum size of 60MB.`;
          onError(error);
          setIsLoadingFiles(false);
          return { success: false, error };
        }
        uploadFormData.append('files', file);
      }

      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const response = await fetchApi.fetch(`${baseUrl}/files/upload`, {
        method: 'POST',
        body: uploadFormData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
          errorData.errors?.map(({ error }: { error: string }) => error).join(', ') || 'Failed to upload files';
        onError(errorMessage);
        setIsLoadingFiles(false);
        return { success: false, error: errorMessage };
      }

      const result = await response.json();
      result.files.forEach((file: any) => {
        uploadedFiles.push(`s3://${file.s3Bucket}/${file.s3Prefix}`);
      });

      // Update form data
      if (formData && Array.isArray(formData)) {
        onChange([...formData, ...uploadedFiles]);
      } else {
        onChange(uploadedFiles);
      }

      setIsLoadingFiles(false);
      return { success: true, uploadedFiles };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      onError(errorMessage);
      setIsLoadingFiles(false);
      return { success: false, error: errorMessage };
    }
  };

  const removeFile = async (index: number, filePath: string): Promise<RemoveFileResult> => {
    if (!formData || !Array.isArray(formData)) {
      return { success: false, error: 'No files to remove' };
    }

    setIsLoadingFiles(true);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const response = await fetchApi.fetch(`${baseUrl}/files/upload`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filePath,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.message || 'Failed to remove file';
        onError(errorMessage);
        setIsLoadingFiles(false);
        return { success: false, error: errorMessage };
      }

      // Update form data by removing the file at the specified index
      const updatedFiles = [...formData];
      updatedFiles.splice(index, 1);
      onChange(updatedFiles);

      setIsLoadingFiles(false);
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      onError(errorMessage);
      setIsLoadingFiles(false);
      return { success: false, error: errorMessage };
    }
  };

  return {
    uploadFiles,
    removeFile,
    isLoadingFiles,
  };
};
